/**
 * Dispatch wraps payloads inconsistently between controllers (bare arrays, `{ list: [] }`, `{ data: [] }`,
 * paged objects with a total). These helpers read them defensively in one place so services stay clean.
 */
export function rowsOf<T = Record<string, unknown>>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  if (!data || typeof data !== 'object') return []
  for (const v of Object.values(data)) if (Array.isArray(v) && (v.length === 0 || typeof v[0] === 'object')) return v as T[]
  for (const v of Object.values(data)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const nested = rowsOf<T>(v)
      if (nested.length) return nested
    }
  }
  return []
}

const TOTAL_KEYS = ['totalCount', 'totalRecords', 'totalRows', 'recordsTotal', 'total', 'count', 'rowsCount', 'totalItems']

/** Finds a total-row count in the envelope pieces (data / data2 / summary) or in the first row. */
export function totalOf(...sources: unknown[]): number | null {
  for (const s of sources) {
    if (typeof s === 'number' && Number.isFinite(s)) return s
    if (s && typeof s === 'object' && !Array.isArray(s)) {
      const lower = new Map(Object.entries(s).map(([k, v]) => [k.toLowerCase(), v]))
      for (const k of TOTAL_KEYS) {
        const v = lower.get(k.toLowerCase())
        if (typeof v === 'number') return v
      }
    }
    if (Array.isArray(s) && s[0] && typeof s[0] === 'object') {
      const lower = new Map(Object.entries(s[0]).map(([k, v]) => [k.toLowerCase(), v]))
      for (const k of TOTAL_KEYS) {
        const v = lower.get(k.toLowerCase())
        if (typeof v === 'number') return v
      }
    }
  }
  return null
}

/** Reads a field regardless of key casing (`AWB`, `awb`, `Awb`). */
export function pick<T = unknown>(row: unknown, ...keys: string[]): T | undefined {
  if (!row || typeof row !== 'object') return undefined
  const obj = row as Record<string, unknown>
  for (const k of keys) if (k in obj) return obj[k] as T
  const lower = new Map(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]))
  for (const k of keys) {
    const v = lower.get(k.toLowerCase())
    if (v !== undefined) return v as T
  }
  return undefined
}

export const num = (v: unknown): number | null => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v))
export const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v))
